import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Calendar, Scale, Droplets, Package, MapPin, Loader2 } from 'lucide-react';
import axios from 'axios';
import { enqueue, isOfflineError } from '../utils/offlineStore';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddHarvestDialog = ({ open, onClose, onSuccess, hives = [], yards = [], token }) => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    hive_ids: [],
    hive_numbers: [],
    yard: '',
    quantity: '',
    unit: 'kg',
    frames_harvested: '',
    honey_type: '',
    quality_grade: '',
    moisture_percent: '',
    storage_location: '',
    notes: '',
    sale_price: '',
    is_sold: false
  });

  useEffect(() => {
    if (open) {
      fetchConfig();
    }
  }, [open]);

  const fetchConfig = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/harvest-config`, { headers });
      setConfig(res.data);
      setFormData(prev => ({ ...prev, unit: res.data.default_unit || 'kg' }));
    } catch (err) {
    }
  };

  // Auto-fill sale price when honey type is selected
  const handleHoneyTypeChange = (honeyType) => {
    const selectedType = config?.honey_types?.find(t => t.name === honeyType);
    const pricePerUnit = selectedType?.price_per_unit || 0;
    setFormData(prev => ({
      ...prev,
      honey_type: honeyType,
      sale_price: pricePerUnit > 0 ? pricePerUnit.toString() : prev.sale_price
    }));
  };

  const handleYardChange = (yardName) => {
    setFormData(prev => ({
      ...prev,
      yard: yardName,
      hive_ids: [],
      hive_numbers: []
    }));
  };

  const handleHiveToggle = (hive, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        hive_ids: [...prev.hive_ids, hive.id],
        hive_numbers: [...prev.hive_numbers, hive.hive_number]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        hive_ids: prev.hive_ids.filter(id => id !== hive.id),
        hive_numbers: prev.hive_numbers.filter(n => n !== hive.hive_number)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      toast.error('Please enter a valid harvest quantity');
      return;
    }

    setLoading(true);
    let payload = null;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      payload = {
        ...formData,
        quantity: parseFloat(formData.quantity),
        frames_harvested: formData.frames_harvested ? parseInt(formData.frames_harvested) : 0,
        moisture_percent: formData.moisture_percent ? parseFloat(formData.moisture_percent) : null,
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null
      };
      
      await axios.post(`${API}/harvests`, payload, { headers });
      toast.success('🍯 Harvest recorded successfully!');
      onSuccess?.();
      onClose();
      
      // Reset form
      setFormData({
        date: new Date().toISOString().split('T')[0],
        hive_ids: [],
        hive_numbers: [],
        yard: '',
        quantity: '',
        unit: config?.default_unit || 'kg',
        frames_harvested: '',
        honey_type: '',
        quality_grade: '',
        moisture_percent: '',
        storage_location: '',
        notes: ''
      });
    } catch (err) {
      if (payload && isOfflineError(err)) {
        await enqueue({
          kind: 'harvest',
          path: '/harvests',
          body: payload,
          label: `Harvest · ${payload.quantity} ${payload.unit}`,
          hiveIds: payload.hive_ids || []
        });
        toast.success('No signal: harvest saved on this phone', { description: 'It will upload automatically when you are back in coverage.' });
        onClose();
        return;
      }
      toast.error('Failed to save harvest');
    } finally {
      setLoading(false);
    }
  };

  const filteredHives = formData.yard 
    ? hives.filter(h => h.yard === formData.yard)
    : hives;

  // Normalize honey types - handle both object format and legacy string format
  const honeyTypes = (config?.honey_types || [])
    .map(t => typeof t === 'string' ? { id: t, name: t, is_active: true } : t)
    .filter(t => t.is_active !== false);
  
  const qualityGrades = (config?.quality_grades || [])
    .map(g => typeof g === 'string' ? { id: g, name: g, is_active: true } : g)
    .filter(g => g.is_active !== false);
  
  const storageLocations = (config?.storage_locations || [])
    .map(s => typeof s === 'string' ? { id: s, name: s, is_active: true } : s)
    .filter(s => s.is_active !== false);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <span className="text-2xl">🍯</span>
            Record Harvest
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Date */}
          <div>
            <Label className="text-stone-700 font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4 text-amber-600" />
              Harvest Date *
            </Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              className="h-11 mt-1"
            />
          </div>

          {/* Apiary & Hives */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <Label className="text-stone-700 font-semibold flex items-center gap-2 mb-3">
              <MapPin className="h-4 w-4 text-amber-600" />
              Source
            </Label>
            
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-stone-600">Apiary</Label>
                <Select value={formData.yard || 'all'} onValueChange={(v) => handleYardChange(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-10 bg-white mt-1">
                    <SelectValue placeholder="Select apiary..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All apiaries</SelectItem>
                    {yards.map(yard => (
                      <SelectItem key={yard.id || yard.name} value={yard.name}>
                        {yard.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Hive Selection */}
              <div>
                <Label className="text-xs text-stone-600">Hives harvested (optional)</Label>
                <div className="mt-2 grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                  {filteredHives.map(hive => (
                    <label
                      key={hive.id}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                        formData.hive_ids.includes(hive.id)
                          ? 'bg-amber-100 border-amber-400'
                          : 'bg-white border-stone-200 hover:bg-stone-50'
                      }`}
                    >
                      <Checkbox
                        checked={formData.hive_ids.includes(hive.id)}
                        onCheckedChange={(checked) => handleHiveToggle(hive, checked)}
                      />
                      <span className="text-sm font-medium">#{hive.hive_number}</span>
                    </label>
                  ))}
                </div>
                {formData.hive_ids.length > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {formData.hive_ids.length} hive(s) selected
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Quantity & Frames */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-stone-700 font-medium flex items-center gap-2">
                <Scale className="h-4 w-4 text-amber-600" />
                Quantity *
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="0.0"
                  required
                  className="h-11 flex-1"
                />
                <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                  <SelectTrigger className="h-11 w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="lbs">lbs</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="oz">oz</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-stone-700 font-medium">Frames Harvested</Label>
              <Input
                type="number"
                min="0"
                value={formData.frames_harvested}
                onChange={(e) => setFormData({ ...formData, frames_harvested: e.target.value })}
                placeholder="0"
                className="h-11 mt-1"
              />
            </div>
          </div>

          {/* Honey Type & Quality */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-stone-700 font-medium">Honey Type</Label>
              <Select value={formData.honey_type} onValueChange={handleHoneyTypeChange}>
                <SelectTrigger className="h-11 mt-1">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {honeyTypes.map(type => (
                    <SelectItem key={type.id || type.name} value={type.name}>
                      {type.name} {type.price_per_unit > 0 && `($${type.price_per_unit}/${formData.unit})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-stone-700 font-medium">Quality Grade</Label>
              <Select value={formData.quality_grade} onValueChange={(v) => setFormData({ ...formData, quality_grade: v })}>
                <SelectTrigger className="h-11 mt-1">
                  <SelectValue placeholder="Select grade..." />
                </SelectTrigger>
                <SelectContent>
                  {qualityGrades.map(grade => (
                    <SelectItem key={grade.id || grade.name} value={grade.name}>
                      {grade.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Sale Price & Sold Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-stone-700 font-medium flex items-center gap-2">
                💰 Sale Price (per {formData.unit})
              </Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.sale_price}
                  onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                  placeholder="0.00"
                  className="h-11 pl-7"
                />
              </div>
              {formData.quantity && formData.sale_price && (
                <p className="text-xs text-green-600 mt-1">
                  Potential revenue: ${(parseFloat(formData.quantity) * parseFloat(formData.sale_price)).toFixed(2)}
                </p>
              )}
            </div>
            <div>
              <Label className="text-stone-700 font-medium">Sale Status</Label>
              <div className="flex items-center gap-3 mt-3 h-11">
                <Checkbox
                  id="is_sold"
                  checked={formData.is_sold}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_sold: checked })}
                />
                <label htmlFor="is_sold" className="text-sm text-stone-600 cursor-pointer">
                  {formData.is_sold ? '✅ Sold' : 'Mark as sold'}
                </label>
              </div>
            </div>
          </div>

          {/* Moisture & Storage */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-stone-700 font-medium flex items-center gap-2">
                <Droplets className="h-4 w-4 text-blue-500" />
                Moisture %
              </Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.moisture_percent}
                onChange={(e) => setFormData({ ...formData, moisture_percent: e.target.value })}
                placeholder="18.5"
                className="h-11 mt-1"
              />
            </div>
            <div>
              <Label className="text-stone-700 font-medium flex items-center gap-2">
                <Package className="h-4 w-4 text-stone-500" />
                Storage
              </Label>
              <Select value={formData.storage_location} onValueChange={(v) => setFormData({ ...formData, storage_location: v })}>
                <SelectTrigger className="h-11 mt-1">
                  <SelectValue placeholder="Select storage..." />
                </SelectTrigger>
                <SelectContent>
                  {storageLocations.map(loc => (
                    <SelectItem key={loc.id || loc.name} value={loc.name}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-stone-700 font-medium">Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Color, aroma, taste observations..."
              className="mt-1 min-h-20"
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  🍯 Record Harvest
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddHarvestDialog;
